/**
 * Are we running inside someone else's frame?
 *
 * A plain synchronous predicate, deliberately NOT a hook and deliberately not
 * part of `useHostBridge`'s state: it has to be readable from imperatively
 * mounted game modules and from transport callbacks that live outside the React
 * tree, and it must be correct on the very first render rather than after a
 * handshake. `window.parent !== window` is true from the first line of script
 * and needs no `init`, no token and no origin allow-list.
 *
 * **What it is used for: deciding who owns navigation.** When this app is
 * embedded, the host is the navigator — it decides which title is on screen and
 * it supplies the way out. Our own hub-linking chrome then stops being a
 * convenience and becomes a door out of whatever the host was enforcing. The
 * content hub (the resident Library) gates its catalog by dementia-fit rating,
 * and a memory-care screen is meant to reach exactly five titles; an "← All
 * games" pill inside the frame reaches all thirteen, including the one the
 * Library's own manifest excludes as unshippable. So: embedded means we render
 * no affordance that navigates to the games hub or to a different game.
 *
 * This is a general rule, not a content-hub special case. CommunityOS
 * (resident-app, signage-player) and the Rendever director tablet also embed
 * this app, and the same reasoning applies to each of them — a host that chose
 * what to put on screen is the host that should decide what happens next.
 * Standalone is untouched: the hub stays fully navigable at
 * games.rendever.com.
 */
export function isEmbedded(): boolean {
  try {
    return typeof window !== 'undefined' && window.parent !== window;
  } catch {
    // Cross-origin access threw — definitely embedded.
    return true;
  }
}
